import { createEndpointer } from './endpointer';
import { languageOf } from './languageOf';
import { rmsOf } from './pcm';
import { createCapture as realCreateCapture } from './capture';

/**
 * THE CLIP EARS. A MediaRecorder note, posted through the `transcribe(blob)` transport the app passes in; the server
 * hears it with gpt-transcribe and names the language. Every customer has these (the capped clip door: 2 MB / 60 s),
 * and the owner's panel falls back to them where the live ears cannot open. While it records, the screen is alive: a
 * level (onLevel) from the same microphone and a state ('recording' → 'hearing' → 'sending').
 *
 *   const ears = startClipEars({ transcribe, onFinal, onState, onError, onLevel, continuous })
 *   await ears.ready            // microphone open and recording
 *   ears.stop()                 // hold released: post the note → onFinal({ text, language })
 *   ears.cancel()               // throw it away
 *   continuous: true            // hands-free: a turn ends itself ~700 ms after speech stops; pause()/resume() gate the mic
 *   ears.close()                // let the microphone go
 *
 * onError gets { code: 'mic-blocked' | 'unsupported' | 'not-heard' | 'hello' | 'limit' | 'failed', cause }.
 */
const TYPES = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'];
export const MAX_NOTE_MS = 55_000; // the server holds a note to 2 MB / about a minute
const IDLE_RESTART_MS = 8_000; // hands-free: silence is not kept; a fresh clip starts so a turn never nears the cap

export function pickRecordingType(R = typeof window !== 'undefined' ? window.MediaRecorder : null) {
  if (typeof R?.isTypeSupported !== 'function') return '';
  return TYPES.find((t) => { try { return R.isTypeSupported(t); } catch (_) { return false; } }) || '';
}

export function canRecordClips() {
  return typeof window !== 'undefined' && typeof window.MediaRecorder === 'function' && !!navigator.mediaDevices?.getUserMedia;
}

const extOf = (type) => (String(type).includes('mp4') ? 'mp4' : String(type).includes('ogg') ? 'ogg' : 'webm');

function codeOfFailure(e) {
  const status = e?.response?.status ?? e?.status;
  if (status === 422 || e?.response?.data?.code === 'AUDIO_NOT_HEARD') return 'not-heard';
  if (status === 403) return 'hello';
  if (status === 429) return 'limit';
  return 'failed';
}

export function startClipEars({ transcribe, onFinal, onState, onError, onLevel, continuous = false, endSilenceMs = 700, deps = {} } = {}) {
  const Rec = deps.MediaRecorder || (typeof window !== 'undefined' ? window.MediaRecorder : null);
  const getStream = deps.getStream || (() => navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 } }));
  const createCapture = deps.createCapture || realCreateCapture;
  const ep = createEndpointer({ endSilenceMs });
  let stream = null; let capture = null; let rec = null; let chunks = []; let startedAt = 0;
  let stopRequested = false; let closed = false; let paused = false; let heard = false; let discard = false; let state = 'connecting';
  const setState = (s) => { if (s !== state) { state = s; onState?.(s); } };
  const fail = (code, cause) => { if (!closed) onError?.({ code, cause }); };

  function begin() {
    chunks = []; heard = false; discard = false; startedAt = Date.now();
    const type = pickRecordingType(Rec);
    rec = type ? new Rec(stream, { mimeType: type }) : new Rec(stream);
    const mine = rec;
    mine.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
    mine.onstop = () => finish(mine);
    mine.start();
  }

  async function finish(mine) {
    const parts = chunks; const dropped = discard || closed;
    chunks = [];
    if (dropped) return;
    const type = (mine.mimeType || parts[0]?.type || 'audio/webm').split(';')[0];
    const blob = new Blob(parts, { type });
    if (!parts.length || blob.size === 0) { fail('not-heard'); resumeListening(); return; }
    blob.name = `voice.${extOf(type)}`;
    setState('sending');
    try {
      const out = await transcribe(blob);
      const text = String(out?.text ?? '').trim();
      const named = String(out?.language ?? '').trim().toLowerCase().slice(0, 2);
      if (closed) return;
      onFinal?.({ text, language: named || languageOf(text) || undefined });
    } catch (e) {
      fail(codeOfFailure(e), e);
    }
    resumeListening();
  }

  function resumeListening() {
    if (!continuous) { release(); if (!closed) setState('idle'); return; }
    if (closed) return;
    ep.reset();
    if (!paused) { setState('listening'); begin(); } else setState('paused');
  }

  function onFrame(frame, rate) {
    if (closed) return;
    const level = rmsOf(frame);
    if (paused) return;
    onLevel?.(level);
    if (!continuous) return;
    const edge = ep.push(level, (frame.length / rate) * 1000);
    if (edge === 'start') { heard = true; setState('hearing'); }
    if (edge === 'end' && rec?.state === 'recording') { setState('sending'); rec.stop(); }
    // Silence is not kept: a clip that has heard nothing for a while is replaced, so a turn never nears the cap.
    else if (!heard && rec?.state === 'recording' && Date.now() - startedAt > IDLE_RESTART_MS) { discard = true; rec.stop(); begin(); }
  }

  const ready = (async () => {
    if (!Rec) { fail('unsupported'); throw new Error('no MediaRecorder'); }
    try {
      stream = await getStream();
    } catch (e) {
      fail(/NotAllowed|Permission|Security/i.test(e?.name || '') ? 'mic-blocked' : 'unsupported', e);
      throw e;
    }
    // Closed while the permission prompt / getUserMedia was pending: let the microphone go at once, record nothing.
    if (closed) { release(); return; }
    try {
      capture = await createCapture({ onFrame, stream });
    } catch (_) { capture = null; /* no level, but the note still records */ }
    if (closed) { release(); return; }
    try { begin(); } catch (e) { stream.getTracks().forEach((t) => t.stop()); fail('unsupported', e); throw e; }
    setState(continuous ? 'listening' : 'recording');
    // A release that came before the recorder existed: honoured now, never left waiting.
    if (stopRequested && rec?.state === 'recording') { stopRequested = false; setState('sending'); rec.stop(); return; }
    if (!continuous) setTimeout(() => { if (!closed && rec?.state === 'recording') ears.stop(); }, MAX_NOTE_MS);
  })();
  ready.catch(() => {});

  const release = () => {
    try { capture?.stop(); } catch (_) { /* gone */ }
    try { stream?.getTracks().forEach((t) => t.stop()); } catch (_) { /* gone */ }
  };

  const ears = {
    ready,
    /** Hold released: post the note. */
    stop() {
      if (closed) return;
      if (!rec) { stopRequested = true; setState('sending'); return; } // the ears are not open yet: stop the moment they are
      if (rec.state !== 'recording') return;
      setState('sending');
      rec.stop(); // the note is posted from the recorder's stop event; the microphone is let go once it has been
    },
    cancel() {
      discard = true;
      try { if (rec?.state === 'recording') rec.stop(); } catch (_) { /* gone */ }
      closed = true; release(); setState('closed');
    },
    pause() { paused = true; ep.reset(); if (continuous && rec?.state === 'recording' && !heard) { discard = true; try { rec.stop(); } catch (_) { /* gone */ } } },
    resume() { paused = false; ep.reset(); if (continuous && !closed && state !== 'sending' && rec?.state !== 'recording') { setState('listening'); begin(); } },
    close() { if (closed) return; discard = true; closed = true; try { if (rec?.state === 'recording') rec.stop(); } catch (_) { /* gone */ } release(); setState('closed'); },
    get state() { return state; },
  };
  return ears;
}
